import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import Popup from 'reactjs-popup';
import '../styles/ftux.css';
import { hasPayoutCap } from '../utils/payoutCap';
import { richTextContentFromHtml } from '../../shared/utils/richTextHtml';

const additionalTermsText = (terms) => {
  if (!terms) return '';
  if (!/<\/?(p|div|br|ul|ol|li|h[1-6]|blockquote|pre|figure|span|a|strong|b|em|i|u|code|del|ins|sub|sup)(\s[^>]*)?\/?>/i.test(terms)) return terms.trim();
  return richTextContentFromHtml(terms).getPlainText().trim();
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPct = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return `${Number(value)}%`;
};

const operatorLabel = (op) => {
  if (op === 'OR') return 'vs';
  if (op === 'AND') return '+';
  return op || '—';
};

const Field = ({ label, value }) => (
  <div className="v3-details-field">
    <div className="v3-details-field__label">{label}</div>
    <div className="v3-details-field__value">{value}</div>
  </div>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

const V3OfferTemplateDetailsModal = ({ open, onClose, team, templateId, isBlueprint, csrfToken }) => {
  const [template, setTemplate] = useState(null);
  const termsText = useMemo(() => additionalTermsText(template?.additional_terms), [template]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !templateId) return;
    setLoading(true);
    setError(null);
    const url = isBlueprint
      ? `/teams/${team.id}/offer_blueprints/v3/${templateId}`
      : `/teams/${team.id}/offer_templates/v3/${templateId}`;
    axios.get(url, { headers: { 'X-CSRF-TOKEN': csrfToken } })
      .then((r) => setTemplate(r.data))
      .catch(() => setError("Couldn't load template details."))
      .finally(() => setLoading(false));
  }, [open, templateId, isBlueprint, team.id, csrfToken]);

  return (
    <Popup
      open={open}
      onClose={onClose}
      modal
      closeOnDocumentClick
      closeOnEscape
      contentStyle={{ width: '560px', maxWidth: 'calc(100vw - 16px)', padding: 0, overflow: 'hidden', border: 'none', borderRadius: '12px', background: 'var(--sl-bg-primary)', color: 'var(--sl-text-primary)' }}
      className="v3-details-popup"
    >
      <div data-testid="v3-template-details-modal" className="v3-details-popup-content">
        <div className="v3-details-header">
          <div style={{ minWidth: 0, flex: 1 }}>
            <span className="v3-details-eyebrow"><i className="far fa-folder-open" /> Template details</span>
            <h2 className="v3-details-title">
              {template?.name || (loading ? 'Loading…' : '—')}
              {template?.is_blueprint && <span className="v3-details-badge--blueprint">Blueprint</span>}
            </h2>
            {template?.description && <p className="v3-details-desc">{template.description}</p>}
          </div>
          <button type="button" className="v3-details-close" onClick={onClose} aria-label="Close">
            <i className="far fa-times" />
          </button>
        </div>

        <div className="v3-details-body">
          {error && <div style={{ color: 'var(--sl-text-error)' }}>{error}</div>}
          {template && !error && (
            <>
              <div className="v3-details-section-label v3-details-section-label--first">Guarantee</div>
              <div className="v3-details-row-grid">
                <Field label="Guarantee" value={formatMoney(template.dollar_amount)} />
                <Field label="Operator" value={operatorLabel(template.dollar_or_percentage_operator)} />
                <Field label="Upside %" value={formatPct(template.upside_percentage)} />
                <Field label="From dollar one" value={template.from_dollar_one ? 'Yes' : 'No'} />
              </div>

              <div className="v3-details-section-label">Revenue base &amp; payout</div>
              <div className="v3-details-row-grid">
                <Field label="Revenue base" value={template.revenue_base || '—'} />
                <Field label="Breakeven threshold" value={formatMoney(template.breakeven_threshold)} />
                {/* 0 means "no cap" — rendering "$0.00" would report a clamp that is not
                    in force. formatMoney already renders an em dash for blank. */}
                <Field label="Payout cap" value={formatMoney(hasPayoutCap(template.artist_payout_cap) ? template.artist_payout_cap : null)} />
                <Field label="Payout floor" value={formatMoney(template.artist_payout_floor)} />
              </div>

              <div className="v3-details-section-label">Merchandising</div>
              <div className="v3-details-row-grid">
                <Field label="Soft merch rate" value={formatPct(template.soft_merch_rate)} />
                <Field label="Hard merch rate" value={formatPct(template.hard_merch_rate)} />
                <Field label="Soft merch sold by" value={template.soft_merch_sold_by || '—'} />
                <Field label="Hard merch sold by" value={template.hard_merch_sold_by || '—'} />
              </div>

              <div className="v3-details-section-label">Tax withholding</div>
              <div className="v3-details-row-grid">
                <Field label="Rate" value={formatPct(template.tax_withholding_percentage)} />
                <Field label="Min threshold" value={formatMoney(template.tax_withholding_minimum_threshold)} />
                <Field label="Due in (days)" value={template.tax_withholding_due_days ?? '—'} />
              </div>

              {Array.isArray(template.deal_term_line_items) && template.deal_term_line_items.length > 0 && (
                <>
                  <div className="v3-details-section-label">Deal terms</div>
                  {template.deal_term_line_items.map((item, idx) => (
                    <div key={item.id || idx} className="v3-details-deal-term">
                      <div className="v3-details-deal-term__label">{item.label}</div>
                      {item.description && <div className="v3-details-deal-term__description">{item.description}</div>}
                    </div>
                  ))}
                </>
              )}

              {termsText && (
                <>
                  <div className="v3-details-section-label">Additional terms</div>
                  <div className="v3-details-additional-terms">{termsText}</div>
                </>
              )}
            </>
          )}
        </div>

        <div className="v3-details-footer">
          <button type="button" className="v3-origin-button" onClick={onClose}>Close</button>
        </div>
      </div>
    </Popup>
  );
};

V3OfferTemplateDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  team: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired }).isRequired,
  templateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isBlueprint: PropTypes.bool,
  csrfToken: PropTypes.string,
};

export default V3OfferTemplateDetailsModal;
